import type { Role } from "./role";

const ACCESS_TOKEN_KEY = "lendcord.accessToken";
const MUST_CHANGE_PASSWORD_KEY = "lendcord.mustChangePassword";

export function storeAccessToken(token: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function clearAccessToken(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
}

// mustChangePassword (AuthResponse) is only ever returned once, at
// login/verify/reset time — unlike role/sub it isn't a JWT claim, so it
// can't be re-derived from the stored token on a page reload. Persisted
// alongside the token so a reload mid-forced-change doesn't let the user
// back into the app; cleared once /auth/password/change succeeds.
export function storeMustChangePassword(mustChangePassword: boolean): void {
  if (mustChangePassword) {
    localStorage.setItem(MUST_CHANGE_PASSWORD_KEY, "true");
  } else {
    localStorage.removeItem(MUST_CHANGE_PASSWORD_KEY);
  }
}

export function getMustChangePassword(): boolean {
  return localStorage.getItem(MUST_CHANGE_PASSWORD_KEY) === "true";
}

// GET /profile has no userId field (only readableId) and POST /profile/reveal
// requires one — the JWT's own `sub` claim is the only place the client can
// get it from (see the Postman collection's "Reveal PII"). Decoded
// defensively since a malformed/expired token should surface as "no id",
// never throw mid-render.
export function getUserIdFromToken(): string | null {
  const token = getAccessToken();
  if (!token) return null;
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload)).sub ?? null;
  } catch {
    return null;
  }
}

// Same decode-defensively approach as getUserIdFromToken above — the JWT's
// `role` claim (see lendcord-core's JwtService.issue) is the only place the
// client can read the caller's role from today; GET /profile doesn't return
// one. Used to gate role-specific UI, e.g. the lender-only "Upgrade to
// Creditor" action — only a LENDER can request that upgrade, per the
// platform's ADMIN -> ENTERPRISE -> {STAFF, AGENT, BORROWER, LENDER} hierarchy.
export function getRoleFromToken(): Role | null {
  const token = getAccessToken();
  if (!token) return null;
  try {
    const payload = token.split(".")[1];
    return (JSON.parse(atob(payload)).role as Role) ?? null;
  } catch {
    return null;
  }
}
